/** When the operator's call banner fires.
 *
 * Requested 2026-10-01, after the banner shipped with these as fixed
 * constants. They were never going to be one number for everybody: an
 * operator at a desk all day wants a short warning, one driving between
 * sites wants a long one, and the right answer is a property of how the
 * org works.
 *
 * Two numbers, stated in plain language rather than labelled "lead" and
 * "grace" — the admin setting them is deciding when they get told, not
 * configuring a scheduler.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";
import type { Organization } from "@/lib/types";

/** Mirrors the server's bounds. A lead of 0 fires as the call starts,
 *  which is not a warning; past an hour it stops meaning "now". Grace
 *  may be 0 — that is "drop it the moment the slot passes". */
const LEAD_MIN = 1;
const LEAD_MAX = 60;
const GRACE_MIN = 0;
const GRACE_MAX = 180;

export function CallAlertsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });

  const [lead, setLead] = useState<string>("");
  const [grace, setGrace] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  // Re-seed only while clean, so a background refetch cannot discard
  // what has been typed.
  useEffect(() => {
    if (org.data && !dirty) {
      setLead(String(org.data.call_alert_lead_minutes));
      setGrace(String(org.data.call_alert_grace_minutes));
    }
  }, [org.data, dirty]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<Organization>(ApiPaths.orgCallAlerts, {
        call_alert_lead_minutes: Number(lead),
        call_alert_grace_minutes: Number(grace),
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
      setDirty(false);
      // The banner polls this; drop its cache so the new window applies
      // without waiting for the next 15s tick.
      void qc.invalidateQueries({ queryKey: ["sessions-live"] });
    },
  });

  if (org.isLoading || !org.data) return null;

  const leadN = Number(lead);
  const graceN = Number(grace);
  const leadBad =
    lead === "" || !Number.isInteger(leadN) || leadN < LEAD_MIN || leadN > LEAD_MAX;
  const graceBad =
    grace === "" ||
    !Number.isInteger(graceN) ||
    graceN < GRACE_MIN ||
    graceN > GRACE_MAX;

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.callAlerts.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t("admin.callAlerts.cardHint")}
        </p>
      </header>

      <div className="flex flex-wrap gap-4">
        <label className="space-y-1 text-xs text-nb-text-2">
          <span className="block">{t("admin.callAlerts.leadLabel")}</span>
          <span className="flex items-baseline gap-1.5">
            <input
              type="number"
              className="input w-20 py-1 text-sm"
              min={LEAD_MIN}
              max={LEAD_MAX}
              value={lead}
              onChange={(e) => {
                setLead(e.target.value);
                setDirty(true);
              }}
              data-testid="call-alerts-lead"
            />
            <span className="text-nb-text-3">
              {t("admin.callAlerts.minutes")}
            </span>
          </span>
          {leadBad && (
            <span className="block text-[11px] text-cvp-danger">
              {t("admin.callAlerts.leadRange", {
                min: LEAD_MIN,
                max: LEAD_MAX,
              })}
            </span>
          )}
        </label>

        <label className="space-y-1 text-xs text-nb-text-2">
          <span className="block">{t("admin.callAlerts.graceLabel")}</span>
          <span className="flex items-baseline gap-1.5">
            <input
              type="number"
              className="input w-20 py-1 text-sm"
              min={GRACE_MIN}
              max={GRACE_MAX}
              value={grace}
              onChange={(e) => {
                setGrace(e.target.value);
                setDirty(true);
              }}
              data-testid="call-alerts-grace"
            />
            <span className="text-nb-text-3">
              {t("admin.callAlerts.minutes")}
            </span>
          </span>
          {graceBad && (
            <span className="block text-[11px] text-cvp-danger">
              {t("admin.callAlerts.graceRange", {
                min: GRACE_MIN,
                max: GRACE_MAX,
              })}
            </span>
          )}
        </label>
      </div>

      <p className="max-w-prose text-[11px] leading-snug text-nb-text-3">
        {graceN === 0
          ? t("admin.callAlerts.graceZeroNote")
          : t("admin.callAlerts.graceNote", { count: graceN || 0 })}
      </p>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={!dirty || leadBad || graceBad || save.isPending}
          onClick={() => save.mutate()}
          data-testid="call-alerts-save"
        >
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
        {save.isError && (
          <span className="text-xs text-cvp-danger">
            {isMfaRequired(save.error)
              ? t("admin.mfaRequiredHint")
              : describeApiError(save.error, t("admin.callAlerts.saveError"))}
          </span>
        )}
      </div>
    </section>
  );
}
